import { z } from 'zod';

// =====================================================================
// Plan 9 — Reconciliation + Acknowledgement schemas
// Spec: docs/superpowers/specs/2026-05-03-discovery-reconciliation-design.md
//   §4.3 ReconciliationReport
//   §4.4 Acknowledgement
// =====================================================================

export const ItemCategorySchema = z.enum([
  'match',
  'missing',
  'extra',
  'model_mismatch',
  'firmware_mismatch',
  'pending_manual_confirmation',
]);

export const ReconciledItemSchema = z.object({
  planned_device_id: z.string().nullable(),
  discovered_serial: z.string().nullable(),
  category: ItemCategorySchema,
  details: z.string(),
  acknowledged: z.object({
    is_acknowledged: z.boolean(),
    acknowledged_by_uid: z.string().nullable(),
    acknowledged_by_email: z.string().nullable(),
    reason: z.string().nullable(),
    acknowledged_at_ms: z.number().int().nullable(),
    age_days: z.number().int().nullable(),
  }),
});

export const ReconciliationReportSchema = z.object({
  run_id: z.string(),
  project_id: z.string(),
  target_id: z.string(),
  generated_at_ms: z.number().int(),
  items: z.array(ReconciledItemSchema),
  summary: z.object({
    total: z.number().int().nonnegative(),
    matches: z.number().int().nonnegative(),
    non_matches: z.number().int().nonnegative(),
    unacknowledged_non_matches: z.number().int().nonnegative(),
  }),
  deploy_decision: z.enum(['allowed', 'blocked']),
  blocked_reason: z.string().nullable(),
});

export const AcknowledgementSchema = z.object({
  ack_key: z.string().min(1),
  category: ItemCategorySchema,
  reason: z.string().min(1),
  acknowledged_by_uid: z.string().min(1),
  acknowledged_by_email: z.string().min(1),
  acknowledged_at_ms: z.number().int(),
});

export const CreateAcknowledgementSchema = z.object({
  ack_key: z.string().min(1),
  category: ItemCategorySchema,
  reason: z.string().min(1, 'Reason is required'),
});

export type ParsedItemCategory = z.infer<typeof ItemCategorySchema>;
export type ParsedReconciledItem = z.infer<typeof ReconciledItemSchema>;
export type ParsedReconciliationReport = z.infer<typeof ReconciliationReportSchema>;
export type ParsedAcknowledgement = z.infer<typeof AcknowledgementSchema>;
export type ParsedCreateAcknowledgement = z.infer<typeof CreateAcknowledgementSchema>;
